import {
  DollarCircleFilled,
  ExperimentFilled,
  IdcardFilled,
  MobileFilled,
  ScheduleFilled,
  SnippetsFilled,
  UnorderedListOutlined,
  DashboardFilled,
  DisconnectOutlined,
  FileProtectOutlined,
  ContainerOutlined,
  CalendarOutlined,
  FormOutlined,
  OrderedListOutlined,
  IssuesCloseOutlined,
  TableOutlined,
  FileAddOutlined,
  DatabaseOutlined,
  AlertFilled,
  MedicineBoxFilled,
  TeamOutlined,
  PhoneOutlined,
  AppstoreOutlined,
  MessageFilled,
  SettingFilled,
  CloudFilled,
  CheckSquareOutlined,
  MedicineBoxOutlined
} from '@ant-design/icons'
import { Layout, Menu } from 'antd'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import { useContext, useState, useEffect, useCallback } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import classNames from 'classnames'

import logo from '../../assets/logo.svg'
import { useUnacknowledgedAlertsCount } from '../../redux/slices/alerts'
import {
  useCurrentUserPracticeId,
  useFeatureEnabled,
  useIsAdmin,
  useCurrentUser,
} from '../../redux/slices/session'
import routes, { toPath } from '../../routes'
import { GetRouteContext } from '../Router/RouterContext'
import { MENU_KEYS } from '../../lib/authorization'
import MenuLink from './MenuLink'
import MenuItem from './MenuItem'
import SubMenu from './SubMenu'

const { Sider } = Layout

const WIDTH = 230

const useStyles = createUseStyles({
  logo: {
    padding: '16px',
    width: 230,
    transition: 'all 0.2s',
    '& img': {
      verticalAlign: 'top',
      maxWidth: '100%',
    },
    background: 'white',
  },
  logoCollapsed: {
    width: 80,
  },
  menu: {
    '& li:first-child': {
      marginTop: 0,
    },
  },
  menuLink: {
    color: 'white',
  },
  subMenuLink: {
    color: 'white',
  },
  menuIcon: {
    fontSize: 18,
    color: 'white',
  },
})

/**
 * A list of each path that needs to be matched to a specific menu item key.
 * Two things are very important to consider:
 * 1. If all subpaths of a specific path will also need to match to the same key,
 * you can just include the parent path (since all subpaths will be included with this)
 * 2. If a more specific subpath needs to be associated with a different key than a parent path,
 * you must put the specific path first, since the search will stop after finding the
 * first path that's equal or a a parent path of the checked path
 */
const PATHS_TO_MENU_KEYS = [
  [routes.alerts(), MENU_KEYS.alerts],
  [routes.billing(), MENU_KEYS.remoteBillingOrders],
  [routes.cycles.cycle.reports.upload(), MENU_KEYS.scheduleReports],
  [
    routes.cycles.cycle.missedTransmissions.new(),
    MENU_KEYS.patientsMissedTransmissions,
  ],
  [
    routes.cycles.cycle.preAuthorizations.new(),
    MENU_KEYS.billingPreauthorizations,
  ],
  [routes.cycles(), MENU_KEYS.scheduleCalendar],
  [routes.deviceModels(), MENU_KEYS.devicesModels],
  [routes.deviceTypes(), MENU_KEYS.devicesTypes],
  [routes.missedTransmissions(), MENU_KEYS.patientsMissedTransmissions],
  [routes.practices.billingOrders(), MENU_KEYS.remoteBillingOrders],
  [routes.practices.cycles(), MENU_KEYS.scheduleCalendar],
  [
    routes.practices.missedTransmissions(),
    MENU_KEYS.patientsMissedTransmissions,
  ],
  [routes.practices.preAuthorizations(), MENU_KEYS.billingPreauthorizations],
  [routes.practices.reports(), MENU_KEYS.scheduleReports],
  [routes.practices(), MENU_KEYS.practices],
  [routes.patients(), MENU_KEYS.patientsList],
  [routes.preAuthorizations(), MENU_KEYS.billingPreauthorizations],
  [routes.reports(), MENU_KEYS.scheduleReports],
  [routes.orders(), MENU_KEYS.orders],
  [routes.pendingReports(), MENU_KEYS.schedulePendingReports],
  [routes.users(), MENU_KEYS.users],
  [routes.home(), MENU_KEYS.dashboard],
  [routes.unreviewedReports(), MENU_KEYS.unreviewedReports],
  [routes.admin.featureFlags(), MENU_KEYS.featureFlags],
  [routes.admin.practiceFeatures(), MENU_KEYS.practiceFeatures],
]

function SideBar({ collapsed }) {
  const classes = useStyles()
  const practiceId = useCurrentUserPracticeId()

  const currentUser = useCurrentUser()

  // NOTE: integrationConfig can change between sessions, create a slice for
  // that if makes more sense;
  const ordersConfig = currentUser?.practice?.integrationConfig?.orders
  const showOrderMenu =
    (typeof ordersConfig === 'boolean' ? ordersConfig : !!ordersConfig?.type) ||
    currentUser?.role === 'admin'

  const { count: unacknowledgedAlertsCount } = useUnacknowledgedAlertsCount()
  const [selectedKeys, setSelectedKeys] = useState([])
  const [openKeys, setOpenKeys] = useState([])
  const match = useContext(GetRouteContext)
  const { brandPrimary } = useTheme()
  const isAdmin = useIsAdmin()

  useEffect(() => {
    const currentKey =
      match &&
      get(
        PATHS_TO_MENU_KEYS.find(pathKey => match.path.includes(pathKey[0])),
        1,
      )
    const openKey =
      currentKey &&
      Object.values(MENU_KEYS).find(key => currentKey.includes(key))
    setSelectedKeys([currentKey])
    setOpenKeys(prevOpenKeys => [...new Set(prevOpenKeys).add(openKey)])
  }, [match])

  const onSelect = useCallback((item, key) => setSelectedKeys([key]), [])
  const onOpenChange = useCallback(keys => setOpenKeys(keys), [])

  return (
    <Sider
      theme="light"
      style={{ background: brandPrimary }}
      trigger={null}
      width={WIDTH}
      collapsible
      collapsed={collapsed}
    >
      <div
        className={classNames(classes.logo, {
          [classes.logoCollapsed]: collapsed,
        })}
      >
        <img src={logo} alt="BioBridge Logo" />
      </div>
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        onOpenChange={onOpenChange}
        onSelect={onSelect}
        className={classes.menu}
        style={{ background: brandPrimary }}
        // if collapsed, we need to avoid passing any value for openKeys (even undefined) or it won't work properly
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(!collapsed && { openKeys })}
      >
        <MenuItem key={MENU_KEYS.dashboard} item={MENU_KEYS.dashboard}>
          <MenuLink
            route={toPath.home()}
            title="Dashboard"
            icon={DashboardFilled}
            color="white"
          />
        </MenuItem>
        <MenuItem key={MENU_KEYS.transmissions} item={MENU_KEYS.transmissions}>
          <MenuLink
            route={toPath.transmissions()}
            title="Recent Transmissions"
            icon={CloudFilled}
            color="white"
          />
        </MenuItem>
        <SubMenu
          item={MENU_KEYS.patients}
          color="white"
          title={
            <span>
              <IdcardFilled className={classes.menuIcon} />
              <span className={classes.menuLink}>Patients</span>
            </span>
          }
        >
          <MenuItem key={MENU_KEYS.patientsList} item={MENU_KEYS.patientsList}>
            <MenuLink
              route={toPath.patients()}
              title="Patients list"
              icon={UnorderedListOutlined}
            />
          </MenuItem>
          <MenuItem key={MENU_KEYS.patientsMissedTransmissions} item={MENU_KEYS.patientsMissedTransmissions}>
            <MenuLink
              route={toPath.missedTransmissions()}
              title="Missed transmissions"
              icon={DisconnectOutlined}
            />
          </MenuItem>
        </SubMenu>

        <SubMenu
          item={MENU_KEYS.billing}
          title={
            <span>
              <DollarCircleFilled className={classes.menuIcon} />
              <span className={classes.menuLink}>Billing</span>
            </span>
          }
        >
          <MenuItem key={MENU_KEYS.billingRemoteAuthorizations} item={MENU_KEYS.billingRemoteAuthorizations}>
            <MenuLink
              route={toPath.preAuthorizations()}
              title="Remote Authorizations"
              icon={FileProtectOutlined}
            />
          </MenuItem>
          <MenuItem key={MENU_KEYS.billingRemoteBillingOrders} item={MENU_KEYS.billingRemoteBillingOrders}>
            <MenuLink
              route={toPath.billing.remoteBillingOrders()}
              title="Remote Billing Orders"
              icon={ContainerOutlined}
            />
          </MenuItem>
          {useFeatureEnabled('in_office_checks') && (
            <MenuItem key={MENU_KEYS.billingOfficeAuthorizations} item={MENU_KEYS.billingOfficeAuthorizations}>
              <MenuLink
                route={toPath.billing.officeAuthorizations()}
                title="Office Authorizations"
                icon={FileProtectOutlined}
              />
            </MenuItem>
          )}
          {useFeatureEnabled('in_office_checks') && (
            <MenuItem key={MENU_KEYS.billingOfficeBillingOrders} item={MENU_KEYS.billingOfficeBillingOrders}>
              <MenuLink
                route={toPath.billing.officeBillingOrders()}
                title="Office Billing Orders"
                icon={ContainerOutlined}
              />
            </MenuItem>
          )}
        </SubMenu>
        <SubMenu
          item={MENU_KEYS.schedule}
          title={
            <span>
              <ScheduleFilled className={classes.menuIcon} />
              <span className={classes.menuLink}>Remote Monitoring</span>
            </span>
          }
        >
          <MenuItem key={MENU_KEYS.scheduleCalendar} item={MENU_KEYS.scheduleCalendar}>
            <MenuLink
              route={
                practiceId
                  ? toPath.practices.cycles({ practiceId })
                  : toPath.cycles()
              }
              title="Remote Schedule"
              icon={CalendarOutlined}
            />
          </MenuItem>
          <MenuItem key={MENU_KEYS.scheduleReports} item={MENU_KEYS.scheduleReports}>
            <MenuLink
              route={toPath.reports()}
              title="Remote Reports"
              icon={FormOutlined}
            />
          </MenuItem>
          <MenuItem key={MENU_KEYS.schedulePendingReports} item={MENU_KEYS.schedulePendingReports}>
            <MenuLink
              route={toPath.pendingReports()}
              title="Pending Reports"
              icon={OrderedListOutlined}
            />
          </MenuItem>
          {showOrderMenu && (
            <MenuItem key={MENU_KEYS.orders} item={MENU_KEYS.orders}>
              <MenuLink
                route={toPath.orders()}
                title="Orders"
                icon={CheckSquareOutlined}
              />
            </MenuItem>
          )}
        </SubMenu>
        <SubMenu
          item={MENU_KEYS.clinicians}
          title={
            <span>
              <ExperimentFilled className={classes.menuIcon} />
              <span className={classes.menuLink}>Clinicians</span>
            </span>
          }
        >
          <MenuItem key={MENU_KEYS.unreviewedReports} item={MENU_KEYS.unreviewedReports}>
            <MenuLink
              route={toPath.unreviewedReports()}
              title="Unreviewed Reports"
              icon={IssuesCloseOutlined}
            />
          </MenuItem>
          <MenuItem key={MENU_KEYS.reviewedSummary} item={MENU_KEYS.reviewedSummary}>
            <MenuLink
              route={toPath.reviewedSummary()}
              title="Reviewed Reports"
              icon={TableOutlined}
            />
          </MenuItem>
        </SubMenu>

        {useFeatureEnabled('in_office_checks') && (
          <SubMenu
            item={MENU_KEYS.officeChecks}
            title={
              <span>
                <SnippetsFilled className={classes.menuIcon} />
                <span className={classes.menuLink}>Office Checks</span>
              </span>
            }
          >
            <MenuItem key={MENU_KEYS.officeChecksAddOfficeCheck} item={MENU_KEYS.officeChecksAddOfficeCheck}>
              <MenuLink
                route={toPath.officeChecks.new()}
                title="Add Office Check"
                icon={FileAddOutlined}
              />
            </MenuItem>
            <MenuItem key={MENU_KEYS.officeChecksOfficeCheckReports} item={MENU_KEYS.officeChecksOfficeCheckReports}>
              <MenuLink
                route={toPath.officeChecks()}
                title="Office Check Reports"
                icon={DatabaseOutlined}
              />
            </MenuItem>
          </SubMenu>
        )}

        <MenuItem key={MENU_KEYS.alerts} item={MENU_KEYS.alerts}>
          <MenuLink
            route={toPath.alerts()}
            title="Alerts"
            badgeCount={unacknowledgedAlertsCount}
            icon={AlertFilled}
            color="white"
          />
        </MenuItem>
        <MenuItem key={MENU_KEYS.practices} item={MENU_KEYS.practices}>
          <MenuLink
            route={toPath.practices()}
            title="Practices"
            icon={MedicineBoxFilled}
            color="white"
          />
        </MenuItem>
        <MenuItem key={MENU_KEYS.users} item={MENU_KEYS.users} className="white-menu">
          <MenuLink
            color="white"
            route={toPath.users()}
            title="Users"
            icon={TeamOutlined}
          />
        </MenuItem>
        <MenuItem key={MENU_KEYS.teams} item={MENU_KEYS.teams}>
          <MenuLink
            color="white"
            route={toPath.teams()}
            title="Teams"
            icon={AppstoreOutlined}
          />
        </MenuItem>
        <SubMenu
          item={MENU_KEYS.devices}
          color="white"
          theme="light"
          title={
            <span>
              <MobileFilled className={classes.menuIcon} />
              <span className={classes.menuLink}>Devices</span>
            </span>
          }
        >
          <MenuItem key={MENU_KEYS.devicesModels} item={MENU_KEYS.devicesModels}>
            <MenuLink
              route={toPath.deviceModels()}
              title="Device models"
              icon={UnorderedListOutlined}
            />
          </MenuItem>
          <MenuItem key={MENU_KEYS.devicesTypes} item={MENU_KEYS.devicesTypes}>
            <MenuLink
              route={toPath.deviceTypes()}
              title="CPT Codes"
              icon={UnorderedListOutlined}
            />
          </MenuItem>
        </SubMenu>
        <SubMenu
          item={MENU_KEYS.welcomeLetters}
          color="white"
          theme="light"
          title={
            <span>
              <SnippetsFilled className={classes.menuIcon} />
              <span className={classes.menuLink}>Welcome Letters</span>
            </span>
          }
        >
          <MenuItem key={MENU_KEYS.welcomeLettersPhoneNumbers} item={MENU_KEYS.welcomeLettersPhoneNumbers}>
            <MenuLink
              route={toPath.welcomeLetters.phoneNumbers()}
              title="Phone Numbers"
              icon={PhoneOutlined}
            />
          </MenuItem>
        </SubMenu>
        <MenuItem key={MENU_KEYS.reminders} item={MENU_KEYS.reminders}>
          <MenuLink
            route={toPath.reminders()}
            title="Reminders"
            icon={MessageFilled}
            color="white"
          />
        </MenuItem>
        {isAdmin && (
          <SubMenu
            item={MENU_KEYS.admin}
            color="white"
            theme="dark"
            title={
              <span>
                <SettingFilled className={classes.menuIcon} />
                <span className={classes.menuLink}>Admin</span>
              </span>
            }
          >
            <MenuItem key={MENU_KEYS.featureFlags} item={MENU_KEYS.featureFlags}>
              <MenuLink
                route={toPath.admin.featureFlags()}
                title="Flag-enabled Features"
                icon={UnorderedListOutlined}
              />
            </MenuItem>
            <MenuItem key={MENU_KEYS.practiceFeatures} item={MENU_KEYS.practiceFeatures}>
              <MenuLink
                route={toPath.admin.practiceFeatures()}
                title="Practice Features"
                icon={MedicineBoxOutlined}
              />
            </MenuItem>
          </SubMenu>
        )}
      </Menu>
      <style>
        {`
        .ant-menu-submenu-inline.white-menu .ant-menu-submenu-title > i.ant-menu-submenu-arrow::before,
        .ant-menu-submenu-inline.white-menu .ant-menu-submenu-title > i.ant-menu-submenu-arrow::after {
          background-image: none;
          background-color: white;
        }
        li.ant-menu-submenu-inline.white-menu ul.ant-menu-sub.ant-menu-inline {
          background: white;
        }
        li.ant-menu-submenu-inline.white-menu .ant-menu-item-only-child {
          padding-left: 24px !important;
        }
        `}
      </style>
    </Sider>
  )
}

SideBar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
}

export default SideBar
